import { useLayoutContext } from 'contexts/LayoutContext';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { investment as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import FormWrapper from 'routes/LpPages/Company/Revaluations/FormWrapper';
import { useRevaluationForm } from 'routes/LpPages/Company/Revaluations/hooks/useRevaluationForm';
import RevaluationFormContent from './RevaluationFormContent';

type RevaluationFormWrapperProps = {
  onPreviousStep: () => void;
  onSaveDraft: () => void;
  onSaveAndApprove: () => void;
  newRevaluations?: rules.CreateInvestmentFormValues['newRevaluations'];
};

const RevaluationFormWrapper: React.FC<
  PropsWithChildren<RevaluationFormWrapperProps>
> = ({
  children,
  onPreviousStep,
  onSaveDraft,
  onSaveAndApprove,
  newRevaluations,
}) => {
  const { vehicle, selectedVehicleId } = useLayoutContext();

  const { watch, setValue } =
    useFormContext<rules.CreateInvestmentFormValues>();
  const assetId = watch('assetId');
  const revaluationOption = watch('revaluationOption');
  const vehicleId = selectedVehicleId ?? '';

  const [asset] = api.asset.get.useSuspenseQuery({
    assetId,
    vehicleId: selectedVehicleId ?? '',
  });

  const [holdingClassInvestmentTotals] =
    api.asset.getAssetHoldingClassInvestmentTotals.useSuspenseQuery({
      assetId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [revaluations] = api.asset.getAssetRevaluations.useSuspenseQuery({
    assetId,
    vehicleId,
  });

  const [assetFullHistory] = api.asset.getAssetFullHistory.useSuspenseQuery({
    assetId,
    vehicleId,
  });

  const [investmentHistory] = api.asset.getInvestmentHistory.useSuspenseQuery({
    assetId,
    vehicleId,
    order: 'desc',
    orderBy: 'dealCompletionDate',
  });

  const defaultRevaluations = holdingClassInvestmentTotals.map(
    (holdingClassInvestmentTotal) => {
      const holdingClassId = holdingClassInvestmentTotal.holdingClassId;
      const holdingClassHistory = assetFullHistory[holdingClassId] ?? [];
      const existingRevaluation = newRevaluations?.find(
        (rev) => rev.holdingClassId === holdingClassId,
      );

      return {
        id: holdingClassId,
        revaluationId: undefined,
        holdingClassId,
        type: RevaluationType.Revaluation as const,
        assetId,
        holdingClassName: holdingClassHistory[0]?.holdingClassName ?? '',
        unitPriceTcy:
          existingRevaluation?.unitPriceTcy ??
          holdingClassInvestmentTotal.latestUnitPrice ??
          0,
        currentHoldValue:
          existingRevaluation?.currentHoldValue ??
          holdingClassInvestmentTotal.currentHoldValue ??
          0,
        totalNumberOfUnits: holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        currentNumberOfUnits:
          holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        ledgerDate: new Date(),
        currency:
          holdingClassInvestmentTotal.currency ??
          asset.baseCurrency ??
          CurrencyCode.GBP,
        isRemoved: false,
        isNew: true,
      };
    },
  );

  const holdingClasses = watch('holdingClasses');

  const formMethods = useRevaluationForm({
    investmentHistory,
    revaluations,
    asset: { baseCurrency: asset.baseCurrency ?? CurrencyCode.GBP },
    newRevaluations: defaultRevaluations,
  });

  if (!vehicle) {
    return null;
  }
  const isViewOnly =
    revaluationOption !== InvestmentRevaluationOption.ManuallyInput;

  const setNewRevaluations = (data: {
    newRevaluations: rules.CreateInvestmentFormValues['newRevaluations'];
    revaluations: rules.CreateInvestmentFormValues['revaluations'];
    investmentHistory: rules.CreateInvestmentFormValues['investmentHistory'];
    revaluationCurrencyRates: rules.CreateInvestmentFormValues['revaluationCurrencyRates'];
  }) => {
    const newRevaluations =
      data.newRevaluations?.filter((rev) => {
        const existingHoldingClass = holdingClassInvestmentTotals.find(
          (hc) => hc.holdingClassId === rev.holdingClassId,
        );
        return existingHoldingClass?.latestUnitPrice !== rev.unitPriceTcy;
      }) ?? [];

    setValue('newRevaluations', newRevaluations);
    setValue('revaluations', data.revaluations);
    setValue('investmentHistory', data.investmentHistory);

    const globalRate = data.revaluationCurrencyRates?.global;
    const revaluationCurrencyRates = globalRate
      ? {
          ...data.revaluationCurrencyRates,
          ...Object.fromEntries(
            holdingClassInvestmentTotals.map((hc) => [
              hc.holdingClassId,
              data.revaluationCurrencyRates?.[hc.holdingClassId] ?? globalRate,
            ]),
          ),
        }
      : data.revaluationCurrencyRates;

    setValue('revaluationCurrencyRates', revaluationCurrencyRates);
  };

  return (
    <FormWrapper
      formMethods={formMethods}
      externalState={{
        selectedVehicle: vehicle,
        assetFullHistory,
      }}
    >
      <RevaluationFormContent
        revaluationOption={revaluationOption}
        investmentHistory={investmentHistory}
        holdingClassInvestmentTotals={holdingClassInvestmentTotals}
        holdingClasses={holdingClasses}
        onPreviousStep={onPreviousStep}
        onSaveDraft={onSaveDraft}
        onSaveAndApprove={(data) => {
          setNewRevaluations({
            newRevaluations: data?.newRevaluations ?? [],
            revaluations: data?.revaluations ?? [],
            investmentHistory: data?.investmentHistory ?? [],
            revaluationCurrencyRates: data?.revaluationCurrencyRates,
          });
          onSaveAndApprove();
        }}
        asset={{
          name: asset.name,
          id: asset.id,
          baseCurrency: asset.baseCurrency ?? CurrencyCode.GBP,
        }}
        vehicle={vehicle}
        vehicleId={vehicleId}
        viewOnly={isViewOnly}
      >
        {children}
      </RevaluationFormContent>
    </FormWrapper>
  );
};

export default RevaluationFormWrapper;
